// Shared playhead-drag hook for Timeline (TTS) and STSMasterTimeline (STS).
//
// The whole vertical playhead shaft is the drag-target so the user doesn't
// have to hit the small head. Pre-refactor both timelines used React's
// pointer events on the element + setPointerCapture; that's functionally
// identical to attaching pointermove/up listeners on the window, which is
// what this hook does internally — exposing only `onPointerDown` for the
// caller to wire to the playhead element.
//
// Pre-refactor flow (preserved):
//   pointerdown  → pause if playing, snapshot the playheadSec into local
//                  drag state so renders override the displayed position
//   pointermove  → recompute sec from clientX + trackRef geometry
//   pointerup    → call seek(finalSec) ONCE; clear drag. Avoids the stop+
//                  restart loop that calling seek() during drag would create
//                  in useTimelinePreview / useSTSMasterPreview.
//
// `playheadDrag` (number | null) is the drag override in seconds. The
// caller selects the displayed playhead via:
//   const display = playheadDrag ?? playheadSec;

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { scheduleVideoScrub } from '@/lib/activeVideo';

function snap01(x: number): number {
  return Math.round(x * 10) / 10;
}

interface Args {
  trackRef: RefObject<HTMLDivElement | null>;
  pxPerSec: number;
  totalDur: number;
  /** Current preview playhead in seconds — used as the initial drag value
   *  on pointerdown so the visible head doesn't jump to the cursor's exact
   *  pixel before the first move event. */
  playheadSec: number;
  isPlaying: boolean;
  stop: () => void;
  seek: (sec: number) => void;
}

export interface UsePlayheadDragReturn {
  /** Drag override in seconds (null when not dragging). */
  playheadDrag: number | null;
  isDragging: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
}

export function usePlayheadDrag({
  trackRef,
  pxPerSec,
  totalDur,
  playheadSec,
  isPlaying,
  stop,
  seek,
}: Args): UsePlayheadDragReturn {
  const [playheadDrag, setPlayheadDrag] = useState<number | null>(null);

  // Refs let the window-bound pointermove/up handlers read the latest
  // pxPerSec/totalDur/seek values without needing to be re-bound each
  // render. (trackRef is itself a stable ref object — used directly.)
  //
  // playheadSec, isPlaying and stop are also mirrored: during playback the
  // onPointerDown callback would only re-bind on isPlaying/stop change, so
  // its closure-captured `playheadSec` would freeze at the value from the
  // play-start moment (typically 0). That stale value got handed to
  // setPlayheadDrag on grab, producing a one-frame jump-to-0 before the
  // first pointermove corrected to the cursor — visible as a short flicker.
  const pxPerSecRef = useRef(pxPerSec);
  pxPerSecRef.current = pxPerSec;
  const totalDurRef = useRef(totalDur);
  totalDurRef.current = totalDur;
  const seekRef = useRef(seek);
  seekRef.current = seek;
  const playheadSecRef = useRef(playheadSec);
  playheadSecRef.current = playheadSec;
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const stopRef = useRef(stop);
  stopRef.current = stop;

  // Track the active pointer id so multi-touch can't cross-fire handlers
  // for a different finger that happens to hit window during the drag.
  const activePointerRef = useRef<number | null>(null);

  const computeSecAt = useCallback(
    (clientX: number): number => {
      const el = trackRef.current;
      const px = pxPerSecRef.current;
      const total = totalDurRef.current;
      if (!el || px <= 0) return 0;
      const rect = el.getBoundingClientRect();
      const x = clientX - rect.left;
      return Math.max(0, Math.min(total, x / px));
    },
    [trackRef],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Pause any running preview — we'll seek() to the final position
      // once on drop. Continuous seek() during drag would stop+restart.
      if (isPlayingRef.current) stopRef.current();

      activePointerRef.current = e.pointerId;
      // Initial drag value = current playheadSec, NOT the pointer position.
      // Snapshot via ref so the value is FRESH (the closure-captured value
      // would be stale during playback — see the ref block above).
      setPlayheadDrag(playheadSecRef.current);

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== activePointerRef.current) return;
        const sec = snap01(computeSecAt(ev.clientX));
        setPlayheadDrag(sec);
        // Live video scrub during drag — restores legacy behavior
        // (legacy/index.html L7012). rAF-throttled + seeking-guarded inside
        // scheduleVideoScrub so a streamed WebDAV source doesn't pile up
        // Range requests on the proxy under fast pointermove.
        scheduleVideoScrub(sec);
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== activePointerRef.current) return;
        const finalSec = snap01(computeSecAt(ev.clientX));
        activePointerRef.current = null;
        setPlayheadDrag(null);
        seekRef.current(finalSec);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [computeSecAt],
  );

  // Cleanup on unmount in case a drag is still active (defensive — the
  // listeners are also removed in onUp under normal flow).
  useEffect(() => {
    return () => {
      activePointerRef.current = null;
    };
  }, []);

  return {
    playheadDrag,
    isDragging: playheadDrag !== null,
    onPointerDown,
  };
}
